// Score challenges (2026-11-03): a shared result link carries the sharer's
// headline number (`?score=241&utm_source=share&utm_medium=score`). With a
// valid score the page shows "Your friend scored 241 ms. Can you beat it?",
// its share image shows the score, and after the visitor's own run both
// numbers sit side by side. We never check the friend's number; we only
// refuse values a real run can't produce, and an invalid one is ignored
// (no banner, static share image).
//
// What a score is and its display belong to the page (the headline of each
// result view); the plausible ranges are generous bounds on what a run can
// produce, for refusing junk only (they judge nothing).
//
// Since 2026-11-04 the page text of a challenge (banner, "You · Your friend",
// share alt text) comes from messages/<locale>/share.json in every language
// (shareScoreText, shareBanner below). The English wording in SHARE_SCORES
// (num/unit/context/verb/ask) is only the English score card image's
// (app/images/tests/[slug]/challenge); other languages' cards use their own
// prerendered text (scripts/make_test_og_images.mjs).
import { FREE_TEST_PAGES } from "@/lib/constants";
import type { BigFiveTrait, FreeTestId } from "@/lib/freeTests";
import type { Translator } from "@/lib/i18n/translate";
import { DEFAULT_LOCALE, splitLocalePath } from "@/lib/i18n/locales";

export interface ShareScoreSpec {
  /** Smallest and largest value a run can produce (inclusive). */
  min: number;
  max: number;
  decimals: 0 | 1;
  /** The number as shown big ("241", "+85", "4-back"). */
  num: (v: number) => string;
  /** The unit next to it ("ms", "digits", ""). */
  unit: (v: number) => string;
  /** Extra words after the score (" on part B"). */
  context?: string | ((v: number) => string);
  /** Verb before the score in "Your friend scored 241 ms" (default "scored"). */
  verb?: (v: number) => string;
  /** The question after it (default "Can you beat it?"). */
  ask?: (v: number) => string;
  /** Width of the analytics bucket for the friend's score. */
  bucket: number;
}

const plain = (v: number) => String(v);
/** Whole seconds as "m:ss" ("2:41"; "1:02:05" from an hour). */
export function clockText(totalSeconds: number): string {
  const s = Math.max(0, Math.round(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, "0");
  return h ? `${h}:${String(m).padStart(2, "0")}:${ss}` : `${m}:${ss}`;
}
const plural = (one: string, many: string) => (v: number) => (v === 1 ? one : many);

// Tests without one headline number (the Big Five profile, 2026-11-04) have no
// entry: their links carry a profile instead (PROFILE_TRAITS below).
export const SHARE_SCORES: Partial<Record<FreeTestId, ShareScoreSpec>> = {
  social_shapes: { min: 0, max: 50, decimals: 0, num: plain, unit: () => "correct", bucket: 1 },
  decision_traps: { min: 0, max: 100, decimals: 0, num: plain, unit: () => "/ 100", bucket: 10 },
  face_memory: { min: 0, max: 100, decimals: 1, num: (v) => v.toFixed(1), unit: () => "%", context: " correct", bucket: 10 },
  emotion_recognition: { min: 0, max: 100, decimals: 1, num: (v) => v.toFixed(1), unit: () => "%", context: " matching intended expressions", bucket: 10 },
  reaction_time: { min: 80, max: 3000, decimals: 0, num: plain, unit: () => "ms", bucket: 50 },
  attention_span: {
    min: 0, max: 400, decimals: 0, num: plain, unit: plural("slow tap", "slow taps"), bucket: 2,
  },
  go_no_go: { min: 0, max: 100, decimals: 0, num: plain, unit: () => "%", context: " right", bucket: 10 },
  n_back: { min: 1, max: 20, decimals: 0, num: (v) => `${v}-back`, unit: () => "", bucket: 1 },
  creativity: { min: 0, max: 100, decimals: 0, num: plain, unit: () => "/ 100", bucket: 10 },
  matrix: { min: 0, max: 50, decimals: 0, num: plain, unit: plural("puzzle", "puzzles"), context: " solved", bucket: 2 },
  number_memory: { min: 0, max: 40, decimals: 0, num: plain, unit: plural("digit", "digits"), bucket: 1 },
  // The mismatch cost; never shown negative: 0 or less reads "No slowdown" (pages share max(0, cost)).
  stroop: {
    min: 0, max: 3000, decimals: 0,
    num: (v) => (v > 0 ? `+${v}` : "No slowdown"),
    unit: (v) => (v > 0 ? "ms" : ""),
    context: (v) => (v > 0 ? " of extra time on mismatched words" : " on mismatched words"),
    verb: (v) => (v > 0 ? "scored" : "had"),
    ask: (v) => (v > 0 ? "Can you beat it?" : "Can you match it?"),
    bucket: 50,
  },
  visual_memory: { min: 0, max: 60, decimals: 0, num: (v) => `level ${v}`, unit: () => "", bucket: 2 },
  sequence_memory: { min: 0, max: 40, decimals: 0, num: plain, unit: plural("square", "squares"), bucket: 1 },
  chimp: { min: 0, max: 60, decimals: 0, num: plain, unit: plural("number", "numbers"), bucket: 2 },
  verbal_memory: { min: 0, max: 2000, decimals: 0, num: plain, unit: plural("word", "words"), bucket: 10 },
  mental_math: { min: 0, max: 300, decimals: 0, num: plain, unit: plural("problem", "problems"), bucket: 5 },
  mental_rotation: { min: 0, max: 100, decimals: 0, num: plain, unit: () => "%", context: " right", bucket: 10 },
  trail_making: { min: 1, max: 600, decimals: 1, num: (v) => v.toFixed(1), unit: () => "s", context: " on part B", bucket: 10 },
  symbol_digit: { min: 0, max: 300, decimals: 0, num: plain, unit: plural("symbol", "symbols"), bucket: 5 },
  // Problems solved in the fewest moves; route efficiency in % (strategy games B, 2026-11-04).
  tower_of_london: {
    min: 0, max: 50, decimals: 0, num: plain, unit: plural("perfect solve", "perfect solves"), bucket: 1,
  },
  sense_of_direction: {
    min: 0, max: 100, decimals: 0, num: plain, unit: () => "%", context: " route efficiency", bucket: 10,
  },
  // Helibrain Daily: the solve time in seconds, shown as a clock ("2:41"); lower is better.
  daily_puzzle: {
    min: 1, max: 21600, decimals: 0, num: (v) => clockText(v), unit: () => "", context: " on Helibrain Daily", bucket: 60,
  },
  // Reading speed: the counted rate per minute (words; characters in languages without spaces).
  reading_speed: { min: 1, max: 2500, decimals: 0, num: plain, unit: () => "wpm", bucket: 25 },
  // IQ-style test: the reasoning percentile in the OMIB research group (the
  // result's headline while that is the only comparison). When the cognitive
  // service switches reasoning to an IQ range, the page shares without a score.
  iq: {
    min: 1, max: 99, decimals: 0, num: (v) => `${v}%`, unit: () => "",
    context: " of a high-scoring research group on reasoning",
    verb: () => "beat",
    ask: () => "Can you beat that?",
    bucket: 10,
  },
  // Decision games (2026-11-04). Guess 2/3: the friend's guess (not a score to beat).
  two_thirds: {
    min: 0, max: 100, decimals: 0, num: plain, unit: () => "",
    verb: () => "guessed", ask: () => "Can you outsmart them?", bucket: 10,
  },
  // Balloon game: adjusted average pumps (pumps per collected balloon).
  balloon_risk: {
    min: 0, max: 128, decimals: 1, num: (v) => v.toFixed(1), unit: () => "pumps", context: " per balloon",
    verb: () => "averaged", ask: () => "How far will you go?", bucket: 10,
  },
  // Card game: net score (good-deck minus bad-deck picks), −100 to +100.
  iowa_gambling: {
    min: -100, max: 100, decimals: 0, num: (v) => (v > 0 ? `+${v}` : v < 0 ? `−${-v}` : "0"), unit: () => "",
    context: " net score", bucket: 10,
  },
  // Speed & skill games (2026-11-04).
  typing_speed: { min: 0, max: 400, decimals: 0, num: plain, unit: () => "WPM", bucket: 10 },
  click_speed: { min: 0, max: 40, decimals: 1, num: (v) => v.toFixed(1), unit: () => "CPS", bucket: 1 },
  aim_trainer: { min: 50, max: 10000, decimals: 0, num: plain, unit: () => "ms", context: " per target", bucket: 50 },
  // Mind's eye: the average vividness of the imagined pictures, 1.0–5.0.
  imagery: {
    min: 1, max: 5, decimals: 1, num: (v) => v.toFixed(1), unit: () => "/ 5",
    context: " for mind's-eye vividness",
    ask: () => "How vivid is yours?",
    bucket: 1,
  },
  // Senses and hype games (2026-11-04). Colour: the hue where blue begins for the
  // sharer (the blue/green boundary, OKLCH degrees within the game's range).
  color_perception: {
    min: 100, max: 300, decimals: 0, num: (v) => `hue ${v}°`, unit: () => "",
    verb: () => "sees blue from",
    ask: () => "Where does your blue begin?",
    bucket: 10,
  },
  // Pitch: the smallest pitch step heard, in cents (lower = finer).
  musical_ear: {
    min: 0.5, max: 400, decimals: 1, num: (v) => v.toFixed(1), unit: () => "cents",
    verb: () => "heard pitch steps of",
    ask: () => "Can you hear smaller ones?",
    bucket: 5,
  },
  perfect_circle: {
    min: 0, max: 100, decimals: 1, num: (v) => v.toFixed(1), unit: () => "%", context: " round",
    verb: () => "drew a circle",
    ask: () => "Can you draw a rounder one?",
    bucket: 5,
  },
  // Time: the average miss over the rounds, in % of the target time (lower = better).
  time_perception: {
    min: 0, max: 200, decimals: 1, num: (v) => v.toFixed(1), unit: () => "%", context: " on average",
    verb: () => "was off by",
    ask: () => "Can you feel time better?",
    bucket: 5,
  },
  // Brain-area games, set G (2026-11-04). Vocabulary: the estimated words known.
  vocabulary: {
    min: 0, max: 200000, decimals: 0, num: (v) => v.toLocaleString("en-US"), unit: () => "",
    context: " words known", bucket: 5000,
  },
  // Task switching: correct answers per minute in the mixed (switching) block.
  task_switching: {
    min: 0, max: 400, decimals: 1, num: (v) => v.toFixed(1), unit: () => "/min",
    context: " right answers while switching", bucket: 10,
  },
  // Daily games: right answers / riddles solved that day.
  knowledge_quiz: {
    min: 0, max: 50, decimals: 0, num: plain, unit: () => "right", context: " on the daily quiz", bucket: 1,
  },
  remote_associates: {
    min: 0, max: 50, decimals: 0, num: plain, unit: plural("riddle", "riddles"), context: " solved today", bucket: 1,
  },
};

/** The share spec of a test that has one (callers check parseShareScore or pass a score first). */
function specOf(test: FreeTestId): ShareScoreSpec {
  const spec = SHARE_SCORES[test];
  if (!spec) throw new Error(`lib/challenge.ts: ${test} has no share score`);
  return spec;
}

const SCORE_RE = /^-?\d{1,5}(\.\d)?$/;

/** The score in the share URL, rounded to the test's precision. */
export function shareScoreParam(test: FreeTestId, value: number): string {
  const spec = specOf(test);
  return spec.decimals ? value.toFixed(spec.decimals) : String(Math.round(value));
}

/** A shared score from the URL, or null when it is missing, malformed or out of range. */
export function parseShareScore(test: FreeTestId, raw: unknown): number | null {
  const s = Array.isArray(raw) ? raw[0] : raw;
  const spec = SHARE_SCORES[test];
  if (!spec || typeof s !== "string" || !SCORE_RE.test(s)) return null;
  const v = Number(s);
  if (!Number.isFinite(v) || v < spec.min || v > spec.max) return null;
  if (!spec.decimals && !Number.isInteger(v)) return null;
  return v;
}

/** "241 ms", "7 digits", "4-back". */
export function formatShareScore(test: FreeTestId, value: number): string {
  const spec = specOf(test);
  const unit = spec.unit(value);
  return unit ? `${spec.num(value)}${unit === "%" ? "" : " "}${unit}` : spec.num(value);
}

/** Words after the score (" on part B"), "" when none. */
export function shareContext(test: FreeTestId, value: number): string {
  const c = specOf(test).context;
  return typeof c === "function" ? c(value) : (c ?? "");
}

/** "scored" / "had" before the score. */
export const shareVerb = (test: FreeTestId, value: number): string => specOf(test).verb?.(value) ?? "scored";

/** "Can you beat it?" (or the test's own question for this score). */
export const shareAsk = (test: FreeTestId, value: number): string =>
  specOf(test).ask?.(value) ?? "Can you beat it?";

/** "Your friend scored 241 ms." / "Your friend had no slowdown on mismatched words." (no question). */
export function friendSentence(test: FreeTestId, value: number): { verb: string; score: string; rest: string } {
  const verb = shareVerb(test, value);
  const shown = formatShareScore(test, value);
  return { verb, score: verb === "scored" ? shown : shown.toLowerCase(), rest: shareContext(test, value) };
}

/** The number of a score in the page's language ("12,3" for 12.3 s in German). */
function shareNumber(tr: Translator, test: FreeTestId, value: number): string {
  const d = specOf(test).decimals;
  return tr.num(value, { minimumFractionDigits: d, maximumFractionDigits: d, useGrouping: false });
}

/** "241 ms", "7 digits", "4-back", "No slowdown", in the page's language (messages share.score_*). */
export function shareScoreText(tr: Translator, test: FreeTestId, value: number): string {
  if (test === "stroop" && value <= 0) return tr.t("share.score_stroop_none");
  if (test === "daily_puzzle") return tr.t("share.score_daily_puzzle", { n: clockText(value) });
  const key = `share.score_${test}`;
  const n = shareNumber(tr, test, value);
  return tr.has(`${key}_other`) ? tr.tp(key, value, { n }) : tr.t(key, { n });
}

/** The banner's message key and its score text ("Your friend scored <b>241 ms</b>. Can you beat it?"). */
export function shareBanner(tr: Translator, test: FreeTestId, value: number): { key: string; score: string } {
  const none = test === "stroop" && value <= 0;
  return { key: none ? "share.banner_stroop_none" : `share.banner_${test}`, score: shareScoreText(tr, test, value) };
}

/** The banner as plain text (share image alt text, link previews). */
export function shareBannerText(tr: Translator, test: FreeTestId, value: number): string {
  const b = shareBanner(tr, test, value);
  return tr.t(b.key, { score: b.score }).replace(/<\/?b>/g, "");
}

/** Analytics bucket of a friend's score, e.g. "200-249". */
export function scoreBucket(test: FreeTestId, value: number): string {
  const step = specOf(test).bucket;
  const lo = Math.floor(value / step) * step;
  return step === 1 ? String(lo) : `${lo}-${lo + step - (specOf(test).decimals ? 0.1 : 1)}`;
}

/**
 * Path of the score card image for a test page and score (og:image of a
 * challenge link). `path` may carry a language prefix ("/es/stroop-test"):
 * the card is then drawn in that language (?locale=es).
 */
export function challengeImagePath(path: string, test: FreeTestId, value: number): string {
  const [locale, base] = splitLocalePath(path);
  const lang = locale === DEFAULT_LOCALE ? "" : `&locale=${locale}`;
  return `/images/tests${base}/challenge?score=${encodeURIComponent(shareScoreParam(test, value))}${lang}`;
}

/**
 * The share link of a test page with a score (always the prod site). `seed`
 * (typing text, aim targets, 2026-11-04): the friend who opens it gets the
 * same text or targets.
 */
export function shareUrl(siteUrl: string, path: string, test: FreeTestId, value: number | null, seed: number | null = null): string {
  if (value === null) return `${siteUrl}${path}`;
  const same = seed === null ? "" : `&seed=${seed}`;
  return `${siteUrl}${path}?score=${encodeURIComponent(shareScoreParam(test, value))}${same}&utm_source=share&utm_medium=score`;
}

const SEED_RE = /^\d{1,16}$/;

/** A shared run's seed from the URL (typing text, aim targets), or null. Its range is checked by the test service. */
export function parseShareSeed(raw: unknown): number | null {
  const s = Array.isArray(raw) ? raw[0] : raw;
  if (typeof s !== "string" || !SEED_RE.test(s)) return null;
  const v = Number(s);
  return Number.isSafeInteger(v) ? v : null;
}

/** The test of a page path, with or without a language prefix. */
export function testForPath(path: string): FreeTestId | null {
  const base = splitLocalePath(path)[1];
  const page = FREE_TEST_PAGES.find((p) => p.href === base);
  return page ? (page.test as FreeTestId) : null;
}

export type SearchParams = Record<string, string | string[] | undefined>;

export interface ChallengeInfo {
  fromEmbed?: boolean;
  test: FreeTestId;
  /** The friend's score, or null when the link had none or an invalid one. */
  score: number | null;
  /** A shared Big Five profile (?traits=…), or null. */
  traits?: TraitProfile | null;
  /** The visitor arrived through a shared score link (valid score or not). */
  landed: boolean;
  /** The friend's run's seed (same typing text / aim targets), when the link had one. */
  seed?: number | null;
}

/** Read a challenge from a test page's URL (server side). */
export function challengeFromParams(path: string, params: SearchParams | undefined): ChallengeInfo | null {
  const test = testForPath(path);
  if (!test || !params) return null;
  if (PROFILE_TESTS.includes(test)) {
    const landed = params.traits !== undefined || params.utm_medium === "profile";
    return landed ? { test, score: null, traits: parseTraits(params.traits), landed, fromEmbed: params.from_embed === "1" } : null;
  }
  const landed = params.score !== undefined || params.utm_medium === "score";
  if (!landed) return null;
  return { test, score: parseShareScore(test, params.score), landed, seed: parseShareSeed(params.seed), fromEmbed: params.from_embed === "1" };
}

// ---- Shared profiles (Big Five, 2026-11-04) ---------------------------------
// A personality result has five numbers and nothing to "beat", so its link
// carries the whole profile: ?traits=O72-C45-E30-A81-S55 (a letter per trait,
// then the number the result showed: the percentile when the test has norms,
// else the 0–100 score; the page reads it on the same scale as its own result).
// The landing page shows "Your friend shared their profile", the friend's
// marks next to the visitor's own after the run, and a share image with the
// five bars (app/images/tests/[slug]/profile). The letters are this link
// format's own; the traits and their order come from the test service.
export const PROFILE_TESTS: readonly FreeTestId[] = ["big_five"];
const TRAIT_LETTERS: Record<BigFiveTrait, string> = {
  openness: "O",
  conscientiousness: "C",
  extraversion: "E",
  agreeableness: "A",
  emotional_stability: "S",
};

export type TraitProfile = Partial<Record<BigFiveTrait, number>>;

/** "O72-C45-E30-A81-S55" for a profile (traits in the order given). */
export function traitsParam(values: { trait: BigFiveTrait; value: number }[]): string {
  return values.map((v) => `${TRAIT_LETTERS[v.trait]}${Math.round(v.value)}`).join("-");
}

/** A shared profile from the URL: all five traits, each 0–100, or null. */
export function parseTraits(raw: unknown): TraitProfile | null {
  const s = Array.isArray(raw) ? raw[0] : raw;
  if (typeof s !== "string" || s.length > 40) return null;
  const out: TraitProfile = {};
  for (const part of s.split("-")) {
    const m = part.match(/^([A-Z])(\d{1,3})$/);
    const trait = m && (Object.keys(TRAIT_LETTERS) as BigFiveTrait[]).find((t) => TRAIT_LETTERS[t] === m[1]);
    if (!m || !trait || trait in out || Number(m[2]) > 100) return null;
    out[trait] = Number(m[2]);
  }
  return Object.keys(out).length === Object.keys(TRAIT_LETTERS).length ? out : null;
}

/** The share link of a profile (always the prod site). */
export function profileShareUrl(siteUrl: string, path: string, traits: string): string {
  return `${siteUrl}${path}?traits=${traits}&utm_source=share&utm_medium=profile`;
}

/** Path of the share image of a profile link (in the page's language). */
export function profileImagePath(path: string, traits: string): string {
  const [locale, base] = splitLocalePath(path);
  const lang = locale === DEFAULT_LOCALE ? "" : `&locale=${locale}`;
  return `/images/tests${base}/profile?traits=${traits}${lang}`;
}
